import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { DnaOff, Radiation, Skull, UserX } from 'lucide-react';
import { memo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useSubmitDuoFeedback } from '@/features/duos/hooks/useSubmitDuoFeedback';
import { useAddSwipedUser } from '@/features/recommend/atoms/swipedUsers';
import { useInvalidateRecommendedPlayers } from '@/features/recommend/hooks/useGetRecommendedPlayers';
import { Button } from '@/shared/components/ui/button';
import {
  Choicebox,
  ChoiceboxItem,
  ChoiceboxItemContent,
  ChoiceboxItemDescription,
  ChoiceboxItemTitle,
} from '@/shared/components/ui/custom/choicebox';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/shared/components/ui/custom/responsive-dialog';
import { Field, FieldError, FieldLabel } from '@/shared/components/ui/field';
import { Textarea } from '@/shared/components/ui/textarea';
import { cn } from '@/shared/utils/cn';
import { getErrorMessage } from '@/shared/utils/errorMessage';

export type ComplaintCategoryId =
  | 'TOXIC_COMMUNICATION'
  | 'GAMEPLAY_SABOTAGE'
  | 'INAPPROPRIATE_PROFILE'
  | 'PLATFORM_ABUSE';

export interface ReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  redirectTo?: string;
}

const complaintCategorySchema = z.enum([
  'TOXIC_COMMUNICATION',
  'GAMEPLAY_SABOTAGE',
  'INAPPROPRIATE_PROFILE',
  'PLATFORM_ABUSE',
]) satisfies z.ZodType<ComplaintCategoryId>;

const complaintSchema = z.object({
  category: complaintCategorySchema,
  comment: z.string().max(1000).optional(),
});

type ComplaintFormValues = z.infer<typeof complaintSchema>;

const COMPLAINT_CATEGORIES: {
  id: ComplaintCategoryId;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    id: 'TOXIC_COMMUNICATION',
    label: 'Toxic Communication',
    description: 'Chat/voice harassment, slurs, insults',
    icon: <Skull className="size-6" />,
  },
  {
    id: 'GAMEPLAY_SABOTAGE',
    label: 'Gameplay Sabotage',
    description: 'Griefing, throwing, or smurfing',
    icon: <Radiation className="size-6" />,
  },
  {
    id: 'INAPPROPRIATE_PROFILE',
    label: 'Inappropriate Profile',
    description: 'Offensive photos, bio, or explicit content',
    icon: <UserX className="size-6" />,
  },
  {
    id: 'PLATFORM_ABUSE',
    label: 'Platform Abuse',
    description: 'Boosting ads, promotions, or illegal activity',
    icon: <DnaOff className="size-6" />,
  },
];

export const ReportModal = memo(function ReportModal(props: ReportModalProps) {
  const { open, onOpenChange, userId, redirectTo } = props;
  const submitFeedback = useSubmitDuoFeedback();
  const invalidateRecommendedPlayers = useInvalidateRecommendedPlayers();
  const addSwipedUser = useAddSwipedUser();
  const navigate = useNavigate();

  const form = useForm<ComplaintFormValues>({
    resolver: zodResolver(complaintSchema),
    defaultValues: {
      category: undefined,
      comment: '',
    },
    mode: 'onChange',
  });

  const category = useWatch({ control: form.control, name: 'category' });
  const isValid = Boolean(category);

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      form.reset();
    }, 300);
  };

  const onSubmit = form.handleSubmit(data => {
    submitFeedback.mutate(
      {
        userId,
        feedback: {
          kind: 'COMPLAINT',
          categories: [data.category],
          ...(data.comment && { comment: data.comment }),
        },
      },
      {
        onSuccess: () => {
          toast.success('User reported & blocked', {
            description: 'thanks for helping keep Gankster safe & fair',
          });
          addSwipedUser(userId);
          handleClose();
          if (redirectTo) {
            void navigate({ to: redirectTo });
          } else {
            void invalidateRecommendedPlayers();
          }
        },
        onError: async error => {
          const errorMessage = await getErrorMessage(error);
          toast.error(`Failed to submit report: ${errorMessage}`);
          handleClose();
        },
      },
    );
  });

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="bg-cold-950 rounded-t-[28px] md:rounded-lg">
        <ResponsiveDialogHeader className="items-start">
          <ResponsiveDialogTitle>Report & Block</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            your feedback helps keep the community clean
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        <div className="flex flex-col gap-6 px-4 pt-6 pb-4">
          <form
            id="report-form"
            onSubmit={onSubmit}
            className="flex flex-col gap-6"
          >
            <Field>
              <Controller
                control={form.control}
                name="category"
                render={({ field }) => (
                  <Choicebox value={field.value} onValueChange={field.onChange}>
                    {COMPLAINT_CATEGORIES.map(category => (
                      <ChoiceboxItem
                        key={category.id}
                        value={category.id}
                        variant="outline"
                        // on selected, add a border-purple-brand-400
                        className={cn(
                          'hover:bg-danger-950 raised raised-0.5 raised-cold-800 border-2',
                          field.value === category.id &&
                            'border-danger-500 bg-danger-950 hover:bg-danger-900 raised-danger-700 border-2',
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {category.icon}
                        </div>
                        <ChoiceboxItemContent>
                          <ChoiceboxItemTitle>
                            {category.label}
                          </ChoiceboxItemTitle>
                          <ChoiceboxItemDescription>
                            {category.description}
                          </ChoiceboxItemDescription>
                        </ChoiceboxItemContent>
                      </ChoiceboxItem>
                    ))}
                  </Choicebox>
                )}
              />
              <FieldError
                error={form.formState.errors.category}
                mode="dirty"
                isDirty={Boolean(form.formState.dirtyFields.category)}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="comment">
                Additional feedback (optional)
              </FieldLabel>
              <Controller
                control={form.control}
                name="comment"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    id="comment"
                    rows={3}
                    maxLength={200}
                    placeholder="Share your thoughts..."
                  />
                )}
              />
              <FieldError
                error={form.formState.errors.comment}
                mode="dirty"
                isDirty={form.formState.dirtyFields.comment}
              />
            </Field>
          </form>
        </div>

        <ResponsiveDialogFooter className="px-4 pb-6">
          <Button
            type="submit"
            form="report-form"
            disabled={!isValid || submitFeedback.isPending}
            className="flex-1"
          >
            {submitFeedback.isPending ? 'Submitting...' : 'Report & Block'}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
});
